import { matchPath } from 'react-router-dom';
import { frontmatterFlag, parseFrontmatter } from './frontmatter';
import { rewriteMarkdown, slugFromFile } from './markdown';
import { withoutFeatureSections } from './feature-sections';

/**
 * The documentation and release notes, read from the repo at build time:
 * `docs/help/**` and `docs/releases/**`. Nothing is fetched; a deploy ships
 * the words with the code they describe.
 */

const HELP_FILES = import.meta.glob('../../../../docs/help/**/*.md', { query: '?raw', import: 'default', eager: true }) as Record<string, string>;
const HELP_IMAGES = import.meta.glob('../../../../docs/help/**/*.{png,jpg,jpeg,gif,webp,svg}', { query: '?url', import: 'default', eager: true }) as Record<string, string>;
const RELEASE_FILES = import.meta.glob('../../../../docs/releases/*.md', { query: '?raw', import: 'default', eager: true }) as Record<string, string>;
const RELEASE_IMAGES = import.meta.glob('../../../../docs/releases/**/*.{png,jpg,jpeg,gif,webp,svg}', { query: '?url', import: 'default', eager: true }) as Record<string, string>;

const HELP_ROOT = '../../../../docs/help/';
const RELEASES_ROOT = '../../../../docs/releases/';

/** Videos come from bcecloud's own CDN only. */
export const HELP_VIDEO_ORIGIN = 'https://cdn.bcecloud.com/';

/** A walkthrough video at the top of a help page. */
export interface HelpVideo {
  readonly src: string;
  /** The frame shown before it plays: the same file with .jpg. */
  readonly poster: string;
}

/**
 * The `video:` frontmatter as a playable video, or undefined when absent or
 * not an MP4 on our CDN (a typo or a foreign URL never renders).
 */
export function helpVideo(value: string | undefined): HelpVideo | undefined {
  const src = value?.trim() ?? '';
  if (!src.startsWith(HELP_VIDEO_ORIGIN) || !/\.mp4$/i.test(src)) return undefined;
  return { src, poster: src.replace(/\.mp4$/i, '.jpg') };
}

/**
 * The `screens:` frontmatter as route patterns: comma-separated, each starting
 * with `/`. Anything else is ignored.
 */
export function helpScreens(value: string | undefined): readonly string[] {
  if (!value) return [];
  return value
    .split(',')
    .map((screen) => screen.trim())
    .filter((screen) => screen.startsWith('/'));
}

export interface HelpDoc {
  readonly area: string;
  readonly slug: string;
  readonly path: string;
  readonly title: string;
  readonly summary: string;
  readonly guide?: string;
  readonly updated?: string;
  /** The switchable feature (Settings → Features) this page documents. */
  readonly feature?: string;
  /** A permission grant the reader must hold to see the page (e.g. `manage_client_portals`). */
  readonly permission?: string;
  /** Where the page sits in its area when the area reads in sequence (a guide); unnumbered pages follow, by title. */
  readonly order?: number;
  readonly video?: HelpVideo;
  /**
   * The app routes this page's video explains (`/proposals`, `/proposals/:id`),
   * from `screens:`. The first is its home: on that screen it plays first.
   */
  readonly screens?: readonly string[];
  /** The body with links and images resolved, ready to render. */
  readonly markdown: string;
  /** Plain-ish text for search. */
  readonly text: string;
}

export interface HelpArea {
  readonly key: string;
  readonly label: string;
  readonly docs: readonly HelpDoc[];
}

export interface Release {
  readonly slug: string;
  readonly title: string;
  readonly date: string;
  readonly announce: boolean;
  /** The switchable feature (Settings → Features) the whole release is about. */
  readonly feature?: string;
  /** A permission grant the reader must hold to see, or be told about, the release. */
  readonly permission?: string;
  /** May carry `<!-- feature: … -->` section markers; read it through `visibleRelease`. */
  readonly markdown: string;
  /** The opening paragraph, for the list. */
  readonly summary: string;
  /** Lower-cased title and body, for search. */
  readonly text: string;
}

/** Images keyed by every path a page might write: relative to its folder, and relative to the root. */
function imageMap(images: Record<string, string>, root: string, folder: string): ReadonlyMap<string, string> {
  const map = new Map<string, string>();
  for (const [file, url] of Object.entries(images)) {
    const fromRoot = file.slice(root.length);
    map.set(fromRoot, url);
    if (fromRoot.startsWith(folder)) map.set(fromRoot.slice(folder.length), url);
  }
  return map;
}

/** The first paragraph of a Markdown body that is prose, not a heading or an image, as plain text. */
function firstParagraph(markdown: string): string {
  const paragraphs = markdown.split(/\n\s*\n/).map((part) => part.trim());
  const prose = paragraphs.find((part) => part.length > 0 && !/^[#!>|-]/.test(part));
  return prose ? plainText(prose) : '';
}

/** Releases whose title or body mention every word of the query. */
export function searchReleases(query: string): readonly Release[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  return allReleases().filter((release) => words.every((word) => release.text.includes(word)));
}

function plainText(markdown: string): string {
  return markdown
    .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/[#>*_`~-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** A label from a folder name: "events" → "Events", "client-portal" → "Client portal". */
function labelFromKey(key: string): string {
  const words = key.replace(/[-_]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** The area order and labels from docs/help/index.md: one `- key: Label` line per area. */
function areaOrder(): readonly { key: string; label: string }[] {
  const index = HELP_FILES[`${HELP_ROOT}index.md`];
  if (!index) return [];
  const { body } = parseFrontmatter(index);
  const areas: { key: string; label: string }[] = [];
  for (const line of body.split('\n')) {
    const match = /^-\s+([a-z0-9-]+)\s*:\s*(.+)$/.exec(line.trim());
    if (match) areas.push({ key: match[1] ?? '', label: (match[2] ?? '').trim() });
  }
  return areas;
}

/** Pages without an `order:` line sort after the numbered ones. */
const UNORDERED = Number.MAX_SAFE_INTEGER;

/** The `order:` frontmatter as a whole number, or undefined when absent or not a number. */
export function pageOrder(value: string | undefined): number | undefined {
  if (value === undefined || !/^\d+$/.test(value.trim())) return undefined;
  return Number(value.trim());
}

let docsCache: readonly HelpDoc[] | undefined;

/** Every help page, in area order then title order. */
export function allDocs(): readonly HelpDoc[] {
  if (docsCache) return docsCache;
  const order = areaOrder();
  const rank = new Map(order.map((area, index) => [area.key, index]));
  const docs: HelpDoc[] = [];
  for (const [file, source] of Object.entries(HELP_FILES)) {
    const rel = file.slice(HELP_ROOT.length);
    if (rel === 'index.md' || rel === 'README.md') continue;
    const parts = rel.split('/');
    if (parts.length < 2) continue;
    const area = parts[0] ?? '';
    const slug = slugFromFile(rel);
    const { data, body } = parseFrontmatter(source);
    const markdown = rewriteMarkdown(body, imageMap(HELP_IMAGES, HELP_ROOT, `${area}/`));
    const order = pageOrder(data['order']);
    const video = helpVideo(data['video']);
    const screens = helpScreens(data['screens']);
    docs.push({
      area,
      slug,
      path: `/help/${area}/${slug}`,
      title: data['title'] ?? slug,
      summary: data['summary'] ?? '',
      ...(data['guide'] ? { guide: data['guide'] } : {}),
      ...(data['updated'] ? { updated: data['updated'] } : {}),
      ...(data['feature'] ? { feature: data['feature'] } : {}),
      ...(data['permission'] ? { permission: data['permission'] } : {}),
      ...(order !== undefined ? { order } : {}),
      ...(video ? { video } : {}),
      ...(screens.length > 0 ? { screens } : {}),
      markdown,
      text: `${data['title'] ?? ''} ${data['summary'] ?? ''} ${plainText(body)}`.toLowerCase(),
    });
  }
  docs.sort(
    (a, b) =>
      (rank.get(a.area) ?? 99) - (rank.get(b.area) ?? 99) ||
      a.area.localeCompare(b.area) ||
      (a.order ?? UNORDERED) - (b.order ?? UNORDERED) ||
      a.title.localeCompare(b.title),
  );
  docsCache = docs;
  return docs;
}

/** The help index: areas in the order index.md lists them, each with its pages. */
export function helpAreas(): readonly HelpArea[] {
  const labels = new Map(areaOrder().map((area) => [area.key, area.label]));
  const byArea = new Map<string, HelpDoc[]>();
  for (const doc of allDocs()) {
    const list = byArea.get(doc.area) ?? [];
    list.push(doc);
    byArea.set(doc.area, list);
  }
  return [...byArea.entries()].map(([key, docs]) => ({ key, label: labels.get(key) ?? labelFromKey(key), docs }));
}

export function findDoc(area: string, slug: string): HelpDoc | undefined {
  return allDocs().find((doc) => doc.area === area && doc.slug === slug);
}

/** Pages whose title, summary or body carry every word of the query. */
export function searchDocs(query: string): readonly HelpDoc[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return allDocs();
  return allDocs().filter((doc) => words.every((word) => doc.text.includes(word)));
}

let releasesCache: readonly Release[] | undefined;

/** Every release note, newest first. */
export function allReleases(): readonly Release[] {
  if (releasesCache) return releasesCache;
  const releases: Release[] = [];
  for (const [file, source] of Object.entries(RELEASE_FILES)) {
    const rel = file.slice(RELEASES_ROOT.length);
    if (rel === 'README.md') continue;
    const { data, body } = parseFrontmatter(source);
    const slug = slugFromFile(rel);
    releases.push({
      slug,
      title: data['title'] ?? slug,
      date: data['date'] ?? slug.slice(0, 10),
      announce: frontmatterFlag(data['announce']),
      ...(data['feature'] ? { feature: data['feature'] } : {}),
      ...(data['permission'] ? { permission: data['permission'] } : {}),
      markdown: rewriteMarkdown(body, imageMap(RELEASE_IMAGES, RELEASES_ROOT, '')),
      summary: firstParagraph(body),
      text: `${data['title'] ?? ''} ${plainText(body)}`.toLowerCase(),
    });
  }
  releases.sort((a, b) => b.date.localeCompare(a.date) || b.slug.localeCompare(a.slug));
  releasesCache = releases;
  return releases;
}

export function findRelease(slug: string): Release | undefined {
  return allReleases().find((release) => release.slug === slug);
}

/** Whether a feature key is on (Settings → Features); keys it does not know are on. */
export type FeatureLookup = (feature: string) => boolean;

/**
 * Whether the reader holds a permission grant. Unknown or still loading means
 * no: a page behind a grant stays hidden until we know the reader may see it.
 */
export type PermissionLookup = (permission: string) => boolean;

/** Whether the reader may see something gated on an optional permission. */
function permitted(permission: string | undefined, has: PermissionLookup): boolean {
  return permission === undefined || has(permission);
}

/**
 * A release as the reader should see it: without the sections of features an
 * admin switched off, and nothing at all when the release is about one of
 * them or every section it had is gone.
 *
 * @param release - The parsed release.
 * @param isOn - Whether a feature is on.
 * @param has - Whether the reader holds a permission; a release behind one they lack is hidden.
 * @returns The release to show, or undefined to hide it.
 */
export function visibleRelease(release: Release, isOn: FeatureLookup, has: PermissionLookup): Release | undefined {
  if (release.feature !== undefined && !isOn(release.feature)) return undefined;
  if (!permitted(release.permission, has)) return undefined;
  const hadSections = /^##\s/m.test(release.markdown);
  const { markdown, sectionsLeft } = withoutFeatureSections(release.markdown, isOn);
  if (hadSections && !sectionsLeft) return undefined;
  return { ...release, markdown, text: `${release.title} ${plainText(markdown)}`.toLowerCase() };
}

/** The releases to show, newest first, optionally narrowed by a search. */
export function visibleReleases(isOn: FeatureLookup, has: PermissionLookup, query = ''): readonly Release[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  return allReleases()
    .map((release) => visibleRelease(release, isOn, has))
    .filter((release): release is Release => release !== undefined)
    .filter((release) => words.every((word) => release.text.includes(word)));
}

/**
 * Whether a help page is shown: not when it documents a switched-off feature,
 * or sits behind a permission the reader lacks.
 */
export function isDocVisible(doc: HelpDoc, isOn: FeatureLookup, has: PermissionLookup): boolean {
  return (doc.feature === undefined || isOn(doc.feature)) && permitted(doc.permission, has);
}

/**
 * The help pages with a video for this screen, most relevant first: pages
 * whose home screen (their first `screens:` entry) is this one, then pages
 * that also cover it, each group in help order.
 *
 * @param pathname - The current route, e.g. `/proposals/6ade…`.
 * @param docs - The pages to choose from (default: every help page).
 * @returns The matching pages that have a video.
 */
export function helpVideosForPath(pathname: string, docs: readonly HelpDoc[] = allDocs()): readonly HelpDoc[] {
  const covers = (pattern: string): boolean => matchPath({ path: pattern, end: true }, pathname) !== null;
  const withVideo = docs.filter((doc) => doc.video !== undefined && (doc.screens ?? []).some(covers));
  const isHome = (doc: HelpDoc): boolean => {
    const home = doc.screens?.[0];
    return home !== undefined && covers(home);
  };
  return [...withVideo.filter(isHome), ...withVideo.filter((doc) => !isHome(doc))];
}
